/* eslint-disable @reatom/unit-naming-rule */
import { atom } from '@reatom/framework'

import { Pnl } from '@blade/finance-core'

import type { Atom } from '@reatom/framework'
import type { Pair } from '@root/entities/pair'
import type BigNumber from 'bignumber.js'

export const reatomPositionPnl = ({
  remainingSizeCurrencyAtom,
  priceAtom,
  entryPriceUsdAtom,
  entryPriceNativeAtom,
  soldSizeUsdAtom,
  soldSizeNativeAtom,
  soldSizeCurrencyAtom,
}: {
  remainingSizeCurrencyAtom: Atom<BigNumber>
  priceAtom: Atom<Pair.Price>
  entryPriceUsdAtom: Atom<BigNumber>
  entryPriceNativeAtom: Atom<BigNumber>
  soldSizeUsdAtom: Atom<BigNumber>
  soldSizeNativeAtom: Atom<BigNumber>
  soldSizeCurrencyAtom: Atom<BigNumber>
}) => {
  const unrealizedPnlUsdAtom = atom((ctx): PnlUsd => {
    const remainingSize = ctx.spy(remainingSizeCurrencyAtom)
    const currentPriceUsd = ctx.spy(priceAtom).priceUsd
    const entryPriceUsd = ctx.spy(entryPriceUsdAtom)

    const { pnlAbsolute, pnlPercentage } = Pnl.unrealized({
      remainingSize,
      entryPrice: entryPriceUsd,
      currentPrice: currentPriceUsd,
    })

    return {
      pnlUsd: pnlAbsolute,
      pnlPercentage,
    }
  })

  const unrealizedPnlNativeAtom = atom((ctx): PnlNative => {
    const remainingSize = ctx.spy(remainingSizeCurrencyAtom)
    const currentPriceNative = ctx.spy(priceAtom).priceNative
    const entryPriceNative = ctx.spy(entryPriceNativeAtom)

    const { pnlAbsolute, pnlPercentage } = Pnl.unrealized({
      remainingSize,
      entryPrice: entryPriceNative,
      currentPrice: currentPriceNative,
    })

    return {
      pnlNative: pnlAbsolute,
      pnlPercentage,
    }
  })

  const realizedPnlUsdAtom = atom((ctx): PnlUsd => {
    const soldSize = ctx.spy(soldSizeCurrencyAtom)
    const soldSizeUsd = ctx.spy(soldSizeUsdAtom)
    const entryPriceUsd = ctx.spy(entryPriceUsdAtom)

    const { pnlAbsolute, pnlPercentage } = Pnl.realized({
      soldSize: soldSize,
      soldValue: soldSizeUsd,
      entryPrice: entryPriceUsd,
    })

    return {
      pnlUsd: pnlAbsolute,
      pnlPercentage,
    }
  })

  const realizedPnlNativeAtom = atom((ctx): PnlNative => {
    const soldSize = ctx.spy(soldSizeCurrencyAtom)
    const soldSizeNative = ctx.spy(soldSizeNativeAtom)
    const entryPriceNative = ctx.spy(entryPriceNativeAtom)

    const { pnlAbsolute, pnlPercentage } = Pnl.realized({
      soldSize: soldSize,
      soldValue: soldSizeNative,
      entryPrice: entryPriceNative,
    })

    return {
      pnlNative: pnlAbsolute,
      pnlPercentage,
    }
  })

  const totalPnlUsdAtom = atom((ctx): PnlUsd => {
    const soldSize = ctx.spy(soldSizeCurrencyAtom)
    const soldValue = ctx.spy(soldSizeUsdAtom)
    const remainingSize = ctx.spy(remainingSizeCurrencyAtom)
    const entryPriceUsd = ctx.spy(entryPriceUsdAtom)
    const currentPriceUsd = ctx.spy(priceAtom).priceUsd

    const { pnlAbsolute, pnlPercentage } = Pnl.total({
      soldSize,
      soldValue,
      remainingSize,
      entryPrice: entryPriceUsd,
      currentPrice: currentPriceUsd,
    })

    return {
      pnlUsd: pnlAbsolute,
      pnlPercentage,
    }
  })

  const totalPnlNativeAtom = atom((ctx): PnlNative => {
    const soldSize = ctx.spy(soldSizeCurrencyAtom)
    const soldValue = ctx.spy(soldSizeNativeAtom)
    const remainingSize = ctx.spy(remainingSizeCurrencyAtom)
    const entryPriceNative = ctx.spy(entryPriceNativeAtom)
    const currentPriceNative = ctx.spy(priceAtom).priceNative

    const { pnlAbsolute, pnlPercentage } = Pnl.total({
      soldSize,
      soldValue,
      remainingSize,
      entryPrice: entryPriceNative,
      currentPrice: currentPriceNative,
    })

    return {
      pnlNative: pnlAbsolute,
      pnlPercentage,
    }
  })

  return {
    unrealizedPnlUsd: unrealizedPnlUsdAtom,
    unrealizedPnlNative: unrealizedPnlNativeAtom,
    realizedPnlUsd: realizedPnlUsdAtom,
    realizedPnlNative: realizedPnlNativeAtom,
    totalPnlUsd: totalPnlUsdAtom,
    totalPnlNative: totalPnlNativeAtom,
    pnlUsd: totalPnlUsdAtom,
    pnlNative: totalPnlNativeAtom,
  }
}

export type PnlUsd = {
  pnlUsd: BigNumber
  pnlPercentage: BigNumber
}
export type PnlNative = {
  pnlNative: BigNumber
  pnlPercentage: BigNumber
}
